import React from "react";
import { Button, Card, Col, DatePicker, Form, Row, Select, Skeleton, Typography } from "antd";
import { useAddressHelper } from "utils/useAddressHelper";
import { ClockCircleOutlined, CheckOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useUpdateEnumeratorAvailability } from "api/enumerator/Availability/useUpdateAvailabilityStatus";
import { useGetEnumeratorAvailabilityStatus } from "api/enumerator/Availability/useGetAvailabilityStatus";
import ErrorPage from "components/ui/ErrorPage";
import dayjs from "dayjs";
import { isJSONString } from "utils/validate-json";
import { formatApiDate } from "utils/date";
import { storage } from "utils/storage";

const { Option } = Select;
const { Title } = Typography;

const AvailabilityForm = () => {
  const {
    address,
    setAddress,
    getProvinceOptions,
    getDistrictOptions,
    getMunicipalityOptions,
    getWardOptions,
  } = useAddressHelper();

  const [form] = Form.useForm();
  const navigate = useNavigate();

  const user = storage.getUserFromStorage();
  const { data, isPending, error } = useGetEnumeratorAvailabilityStatus(user.uid);
  const updateStatusMutation = useUpdateEnumeratorAvailability();

  const handleFinish = async () => {
    const values = await form.validateFields();

    const payload = {
      uid: user?.uid,
      isAvailable: {
        status: true,
        availableLocation: {
          ...values.availableLocation,
          state: isJSONString(values?.availableLocation?.state)
            ? JSON.parse(values.availableLocation.state)?.name
            : values.availableLocation.state,
        },
        availableFrom: formatApiDate(values.availableFrom),
        availableTo: formatApiDate(values.availableTo),
      }
    };

    updateStatusMutation.mutate(payload, {
      onSuccess: () => {
        navigate("/enumerator/home");
      },
    });
  };

  React.useEffect(() => {
    if (!data) return;

    form.setFieldsValue({
      availableLocation: data?.availableLocation,
      availableFrom: data?.availableFrom ? dayjs(data.availableFrom) : dayjs(),
      availableTo: data?.availableTo ? dayjs(data.availableTo) : dayjs(),
    });
  }, [data, form]);

  if (isPending) {
    return (
      <Card style={{ width: "100%", marginTop: 16, height: "580px" }}>
        <Skeleton active />
        <Skeleton active />
        <Skeleton active />
        <Skeleton active />
      </Card>
    );
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      <Card className="mt-6 W-full ">
        <Title level={2}>Available location</Title>
        <Form layout="vertical" form={form} onFinish={handleFinish} className="mt-10" requiredMark>
          <Row gutter={[48, 32]}>
        <Col xs={24} sm={24} md={24} lg={12} xl={12}>
          <Form.Item label="Province" name={["availableLocation", "state"]} rules={[{ required: true, message: 'Please select a province' }]}>
            <Select
          allowClear
          placeholder="Select province"
          options={getProvinceOptions()}
          onChange={(value: string) => setAddress({ ...address, province: value })}
          style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24} lg={12} xl={12}>
          <Form.Item name={["availableLocation", "district"]} label="District" rules={[{ required: true, message: 'Please select a district' }]}>
            <Select
          allowClear
          showSearch
          placeholder="Select district"
          style={{ width: "100%" }}
          optionFilterProp="children"
          filterOption={(input, option: any) => {
            return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
          onChange={(value: string) => setAddress({ ...address, district: value })}
            >
          {getDistrictOptions()?.map((district) => (
            <Option key={district?.label} value={district?.label}>
              {district?.label}
            </Option>
          ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24} lg={12} xl={12}>
          <Form.Item name={["availableLocation", "municipality"]} label="Municipality" rules={[{ required: true, message: 'Please select a municipality' }]}>
            <Select
          allowClear
          showSearch
          placeholder="Select municipality"
          style={{ width: "100%" }}
          optionFilterProp="children"
          filterOption={(input, option: any) => {
            return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
          onChange={(value: string) => setAddress({ ...address, municipality: value })}
            >
          {getMunicipalityOptions()?.map((municipality) => (
            <Option key={municipality?.label} value={municipality?.label}>
              {municipality?.label}
            </Option>
          ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24} lg={12} xl={12}>
          <Form.Item name={["availableLocation", "ward"]} label="Ward" rules={[{ required: true, message: 'Please select a ward' }]}>
            <Select
          allowClear
          placeholder="Select ward"
          options={getWardOptions()}
          style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={24} lg={12} xl={12}>
          <Form.Item name="availableFrom" label="Available Day" rules={[{ required: true, message: 'Please select a start date' }]}>
            <DatePicker className="w-full" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={24} lg={12} xl={12}>
          <Form.Item name="availableTo" label="Available Until" rules={[{ required: true, message: 'Please select an end date' }]}>
            <DatePicker className="w-full" />
          </Form.Item>
        </Col>
          </Row>

          <div className="flex flex-col md:flex-row gap-y-4 justify-between  pt-12">
        <Button
          className="h-12 px-8  border-2 border-border text-base  flex items-center justify-center gap-2"
          onClick={() => navigate("/enumerator/home")}
        >
          <ClockCircleOutlined />
          Cancel
        </Button>
        <Button
          className="h-12 px-8 border-2 border-border text-base  flex items-center justify-center gap-2 bg-primary text-white border-none"
          htmlType="submit"
          loading={updateStatusMutation.isPending}
          type="primary"
        >
          <CheckOutlined />
          Submit
        </Button>
          </div>
        </Form>
      </Card>
    </>
  );
};

export default AvailabilityForm;
